import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import HTMLFlipBook from "react-pageflip";
import {
  Rocket,
  Building,
  Award,
  Target,
  Users,
  Network,
  ArrowRight,
  Lightbulb,
  TrendingUp,
  CheckCircle,
  Code,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Quote,
} from "lucide-react";

import {
  timelineDates,
  timelineMilestones,
  dignitaryQuotes,
  participantFeedback,
  whatsNew,
  achievements,
} from "../mockData";
import AOS from "aos";
import "aos/dist/aos.css";

const FeaturedUseCases = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);

    // Warn user before leaving if there are unsaved changes
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
      }
    }, 4000); // auto scroll every 4s

    return () => clearInterval(interval);
  }, []);
};
const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div
      className="bg-gradient-to-br from-[#111827] to-[#1f2937] rounded-2xl  overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
      data-aos="fade-up"
      data-aos-delay={index * 50}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors duration-200"
      >
        <h4 className="text-lg font-semibold text-white text-left">
          {question}
        </h4>
        <ChevronDown
          className={`w-5 h-5 text-red-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {isOpen && (
        <div className="px-6 pb-6 text-gray-300 border-t border-white/10">
          <div className="leading-relaxed pt-4">
            {answer.includes("<a") ? (
              <span dangerouslySetInnerHTML={{ __html: answer }} />
            ) : (
              <p>{answer}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
const benefits = [
  {
    id: 1,
    icon: Lightbulb,
    title: "Turn ideas into reality",
    description:
      "Work on real ONGC problem statements and see ideas move beyond discussion into usable solutions, with Udbhav team's support.",
    number: "01",
  },
  {
    id: 2,
    icon: TrendingUp,
    title: "Learn and Grow your Career",
    description:
      "Learn hands-on AI, collaborate across departments, and build skills that go beyond your current designation or daily responsibilities.",
    number: "02",
  },
  {
    id: 3,
    icon: Award,
    title: "Get noticed for meaningful work",
    description:
      "Present your work to senior leadership and get recognized and known.",
    number: "03",
  },
];
const projects = [
  {
    id: "U1UCI345",
    image: "/images/3.png",
    title:
      "AI Powered Hole Cleaning Monitoring System using Physics-based & Computer Vision Models",
    team: "Drishti",
    description:
      "Provides real-time hole cleaning indices using physics-based models and AI-driven computer vision to enable instant operational adjustments for safe and efficient drilling.",
    features: [
      "Real-time hole cleaning indices (ECD, CCI, Transport Ratio, Cuttings Concentration)",
      "Physics-based hole cleaning estimation models",
      "Computer vision-based drill cuttings estimation from solids control video",
      "Instant optimization of drilling and drilling fluid parameters",
    ],
    whyRequired: [
      "No real-time hole cleaning monitoring system exists today",
      "Manual cuttings estimation is subjective, delayed, and inaccurate",
      "Poor hole cleaning causes stuck pipe, losses, formation damage, and low ROP",
    ],
    benefits: [
      "Improved ROP through real-time drilling optimization",
      "Reduced downhole complications",
      "Improved HSE by reducing hazardous manual exposure",
      "Strong digital transformation with measurable ROI",
    ],
  },

  {
    id: "U1UCI115",
    image: "/images/17.png",
    title: "Agentic AI for EOR-Focused Reservoir Management",
    team: "Drishtant",
    description:
      "An AI-driven reservoir management suite using Agentic AI to automate EOR screening, data preparation, insight generation, and performance monitoring.",
    features: [
      "Agentic AI framework for EOR workflows",
      "ML-based EOR screening and ranking",
      "Automated simulation-ready data preparation",
      "AI-enabled log processing and document insights (InsightGPT, EOR Guru)",
      "Unified EOR and field performance dashboards",
    ],
    whyRequired: [
      "Manual EOR studies are time-consuming",
      "Decisions rely on scattered data and expert judgment",
      "Limited capability to compare multiple EOR scenarios consistently",
    ],
    benefits: [
      "Improved quality and consistency of EOR decisions",
      "Reduced analysis time and manual effort",
      "Enhanced oil recovery using data-driven insights",
      "Faster reservoir management workflows",
    ],
  },

  {
    id: "U1UCI392",
    image: "/images/7.png",
    title:
      "AI Enabled Onshore Exploratory Well Cost Planning and Variance Analysis",
    team: "Well AI",
    description:
      "Creates fast and reliable exploratory well budgets using historical data, while tracking real-time cost variance with early warnings and clear explanations.",
    features: [
      "AI-based component-wise well cost planning",
      "Robust physical and financial metrics database",
      "Real-time variance analysis (plan vs actual)",
      "Uniform planning templates",
      "Web-based SAS dashboard with SAP integration",
    ],
    whyRequired: [
      "Difficulty benchmarking estimated costs with historical wells",
      "Manual variance analysis is time-intensive",
      "Lack of structured templates and centralized data",
    ],
    benefits: [
      "Early identification of cost overruns",
      "Improved budget utilization and planning",
      "Better cost recovery confidence with JV partners and DGH",
      "Significant man-hour savings",
    ],
  },

  {
    id: "U1UCI512",
    image: "/images/2.png",
    title:
      "AI-Enabled Physics-Based Optimization of Water Injection in Oil Reservoirs",
    team: "Optiinject",
    description:
      "Recommends safe, small water-injection adjustments using hybrid Physics + AI to improve oil recovery while maintaining reservoir pressure limits.",
    features: [
      "Hybrid Physics + AI optimization framework",
      "Injector–producer influence mapping",
      "Event-triggered adaptive learning",
      "Field, sector, and well-level optimization",
      "Integrated forecasting and optimization",
    ],
    whyRequired: [
      "Need for improved oil recovery with minimal CAPEX",
      "Lack of unified reservoir intelligence platform",
      "Requirement for explainable and scalable digital twin foundation",
    ],
    benefits: [
      "Improved injector efficiency and faster decisions",
      "Scalable, modular architecture deployable across fields",
      "Aligned with ONGC Digital Oilfield initiatives",
    ],
  },

  {
    id: "U1UCI013",
    image: "/images/20.png",
    title: "Seismic Imaging through Full Waveform Inversion (FWI)",
    team: "GeoPulse",
    description:
      "An in-house Full Waveform Inversion solution enhanced with AI/ML to generate high-resolution subsurface velocity models and reduce vendor dependency.",
    features: [
      "High-resolution 2D/3D waveform inversion",
      "Efficient wave equation solvers with absorbing boundaries",
      "GPU/CPU parallel processing",
      "AI-based memory-efficient wavefield compression",
    ],
    whyRequired: [
      "Complete dependency on third-party FWI vendors",
      "High software, processing, and AMC costs",
      "Vendor-driven timelines and limited flexibility",
    ],
    benefits: [
      "Reduced seismic processing costs",
      "Improved subsurface interpretation accuracy",
      "Lower drilling risks and better resource estimation",
    ],
  },

  {
    id: "U1UCI295_322",
    image: "/images/13.png",
    title: "Well Intervention Opportunities Generation using AI",
    team: "Wisdom",
    description:
      "AI-based workflow that scores and ranks wells for intervention using production, logs, and historical job data for faster and better ROI decisions.",
    features: [
      "Unified production, reservoir, and intervention data",
      "Transparent opportunity scoring for flowing and shut-in wells",
      "Interactive uplift-based dashboards",
      "Domain-rule validation layer",
      "Supervised ML model (~87% accuracy)",
    ],
    whyRequired: [
      "Manual screening is slow and inconsistent",
      "High-ROI wells often missed",
      "Approval cycles are lengthy and subjective",
    ],
    benefits: [
      "~80% reduction in evaluation time",
      "100% well stock coverage per cycle",
      "Avoidance of low-ROI jobs",
      "Improved rig sequencing and planning",
    ],
  },

  {
    id: "U1UCI255_257_390",
    image: "/images/5.png",
    title: "Missed Zone Identification",
    team: "Payzone AI",
    description:
      "ML-based system that identifies hydrocarbon-bearing missed zones using well logs and production data to revive non-performing wells.",
    features: [
      "Ingestion of decades of logs and production data",
      "Automated ranking of missed pay intervals",
      "Targeting for well revival and production uplift",
      "Perforation window recommendations using analogs",
    ],
    whyRequired: [
      "Need to increase production from existing wells",
      "Large inventory of non-flowing and suboptimal wells",
      "Manual zone identification is time-consuming",
    ],
    benefits: [
      "Unbiased AI-driven zone identification",
      "Production enhancement with minimal cost",
      "Significant reduction in engineering man-hours",
    ],
  },

  {
    id: "U1UCI447B",
    image: "/images/6.png",
    title: "Real-Time Inventory Forecasting and Optimization",
    team: "Inv_Optima",
    description:
      "Real-time inventory forecasting and optimization system that minimizes stock-outs and excess holding costs with automated procurement triggers.",
    features: [
      "Demand forecasting for Class B & C items",
      "EOQ and safety stock auto-calculation",
      "Vendor-wise ROP triggering and consolidation",
      "Lead time and reliability analysis",
    ],
    whyRequired: [
      "No consumption-based forecasting system",
      "Inaccurate requisitions and poor prioritization",
      "Limited coordination between field and procurement",
    ],
    benefits: [
      "Progressive reduction in inventory",
      "Stock-out rate below 2%",
      "Systematic and auditable procurement",
      "Real-time inventory visibility",
    ],
  },

  {
    id: "U1UCI287",
    image: "/images/1.png",
    title:
      "AI Powered Optimization of Well Stimulation Jobs for Hydrocarbon Recovery",
    team: "WellStim Wizard",
    description:
      "AI-driven analysis of historical stimulation jobs to predict success and optimize future stimulation design.",
    features: [
      "ML models to predict stimulation success or failure",
      "Analysis of job parameters and reservoir properties",
      "Early rejection of low-probability jobs",
      "Dashboard-based planning insights",
    ],
    whyRequired: [
      "High failure rate of stimulation jobs",
      "Planning relies heavily on experience",
      "No systematic learning from past jobs",
    ],
    benefits: [
      "Higher stimulation success rates",
      "Reduced operational expenditure",
      "Consistent and objective planning across assets",
    ],
  },

  {
    id: "U1UCI263",
    image: "/images/14.png",
    title: "AI-Assisted Drill Bit Grading and Evaluation",
    team: "MargDarsh",
    description:
      "AI-powered drill bit grading system that digitizes cutter wear and enables data-driven bit selection and drilling optimization.",
    features: [
      "Automated drill bit grading and evaluation",
      "Digitization of cutter wear patterns",
      "Analytics-driven bit and BHA optimization",
    ],
    whyRequired: [
      "Manual grading is subjective and time-consuming",
      "Limited data captured from used bits",
      "Delayed and inconsistent decisions",
    ],
    benefits: [
      "Reduced drill bit costs",
      "Improved drilling ROP",
      "Supports digital transformation with ROI",
    ],
  },

  {
    id: "U1UCI044",
    image: "/images/18.png",
    title: "Facies Characterisation using Machine Learning",
    team: "Lithomind",
    description:
      "MDT-driven ML workflow for facies prediction to improve reservoir characterization and support drilling and development decisions.",
    features: [
      "ML-based facies prediction eliminating manual bias",
      "MDT-driven validation",
      "Field-specific facies templates",
      "Reusable AI framework",
    ],
    whyRequired: [
      "Manual facies interpretation is slow and inconsistent",
      "Complex non-linear subsurface relationships",
      "Need for real-time decision support",
    ],
    benefits: [
      "Faster subsurface insights",
      "More accurate reservoir models",
      "Reusable framework across basins",
    ],
  },
  {
    id: "U1UCI196",
    image: "/images/TCO Image .png",
    title: "AI Driven Optimization of Air Travel Cost",
    team: "TravelOpt AI",
    description:
      "An AI-driven solution that analyzes historical air travel data to optimize employee travel planning and reduce overall travel expenditure by recommending efficient and policy-compliant travel options.",
    features: [
      "Analysis of historical travel patterns and expenditure",
      "Identification of cost-effective routes, airlines, and booking windows",
      "Policy-aligned travel recommendations",
      "Dashboard-based visibility of travel cost trends and savings",
    ],
    whyRequired: [
      "Rising air travel costs across locations and functions",
      "Manual booking decisions lack cost optimization insights",
      "Limited visibility into travel spend patterns and inefficiencies",
      "Difficulty in tracking and controlling avoidable premium bookings",
    ],
    benefits: [
      "Reduction in overall air travel expenditure",
      "Improved budget control and cost transparency",
      "Data-driven and consistent travel decision-making",
      "Optimized and efficient employee travel planning",
    ],
  },
  {
    id: "U1UCI077",
    image: "/images/8.png",
    title: "AI/ML Based Candidate Well Selection for Well Intervention",
    team: "Intervene AI",
    description:
      "An AI/ML-based solution that evaluates production trends, reservoir parameters, and historical intervention data to identify and rank wells with the highest potential for successful intervention and production uplift.",
    features: [
      "Integration of production data, reservoir indicators, and past intervention outcomes",
      "Machine learning–based ranking of candidate wells",
      "Automated shortlisting of flowing and non-flowing wells",
      "Dashboard-based visualization for comparison and review",
    ],
    whyRequired: [
      "Manual well screening is time-consuming and varies across engineers and assets",
      "High-potential intervention candidates may be missed due to data overload",
      "Lack of standardized and objective well selection methodology",
      "Risk of executing low-impact or non-viable intervention jobs",
    ],
    benefits: [
      "Improved success rate of well interventions",
      "Reduced evaluation time and manual effort",
      "Optimized utilization of intervention resources and rigs",
      "Consistent and data-driven decision-making across assets",
    ],
  },

  {
    id: "U1UCI366",
    image: "/images/19.png",
    title: "Smart Drilling Fluid Management using AI & Digital Techniques",
    team: "Digi Fluids",
    description:
      "Near real-time digital advisory system for drilling fluid management replacing delayed manual checks.",
    features: [
      "Digital mud test and dosing workflow",
      "Explainable PV/YP predictions",
      "Trend analysis and alerts",
      "Auditable action history",
    ],
    whyRequired: [
      "Delayed manual mud checks (6–8 hours)",
      "Lack of real-time optimization",
    ],
    benefits: [
      "Reduced NPT",
      "Optimized chemical costs",
      "Improved safety and well stability",
    ],
  },
  {
    id: "U1UCI010_151",
    image: "/images/11.png",
    title:
      "Android Mobile Application for Tanker Loading / Unloading & Crude Oil Truck Movement Analysis",
    team: "MoveOil",
    description:
      "A mobile-based digital application designed to capture, monitor, and analyze tanker loading and unloading operations by recording real-time transaction data related to crude oil truck movement, improving visibility, traceability, and operational control.",
    features: [
      "Digital capture of tanker loading and unloading data through a mobile application",
      "Real-time recording of quantities, timestamps, and locations",
      "Centralized data repository for monitoring crude oil truck movement",
      "Analytics and reporting for operational review and audits",
    ],
    whyRequired: [
      "Manual recording of tanker operations leads to errors and delays",
      "Limited transparency in crude oil truck movement and transactions",
      "Difficulty in reconciling field data with inventory and dispatch records",
      "Compliance, audit, and monitoring challenges with paper-based processes",
    ],
    benefits: [
      "Improved transparency and traceability of tanker operations",
      "Reduction in manual errors and data inconsistencies",
      "Enhanced monitoring and control of crude oil movement",
      "Faster audits and improved operational decision-making",
    ],
  },

  {
    id: "U1UCI458",
    image: "/images/12.png",
    title: "AI-Driven Multi-Sensor System for 24/7 Mud Pump Monitoring",
    team: "Predictive Pulse",
    description:
      "24/7 mud pump monitoring using vibration and ultrasound sensors to detect early faults and prevent unplanned downtime.",
    features: [
      "Continuous vibration and ultrasound monitoring",
      "Early detection of mud cut and fatigue",
      "Explainable alerts and trendlines",
      "Auditable dashboards",
    ],
    whyRequired: [
      "No predictive monitoring for mud pump fluid end",
      "Delayed detection leads to severe damage and downtime",
    ],
    benefits: [
      "Reduced unplanned downtime",
      "Lower repair and replacement costs",
      "Reduced spare inventory",
    ],
  },
  {
    id: "U1UCI236",
    image: "/images/4.png",
    title: "Optimization and Anomaly Detection of Surface Gas Network",
    team: "Wadu AI",
    description:
      "An AI-driven solution that monitors surface gas networks in real time to detect anomalies, inefficiencies, and imbalances, enabling optimized and reliable gas network operations.",
    features: [
      "Real-time monitoring of surface gas network parameters",
      "AI-based detection of anomalies and flow imbalances",
      "Identification of inefficiencies and abnormal operating conditions",
      "Dashboard-based visualization for operators and planners",
    ],
    whyRequired: [
      "Manual monitoring can miss early signs of gas losses or anomalies",
      "Complex gas networks are difficult to analyze using conventional methods",
      "Lack of real-time insights delays corrective actions",
      "Unnoticed inefficiencies impact operational performance",
    ],
    benefits: [
      "Reduced gas losses and operational inefficiencies",
      "Improved reliability and stability of gas networks",
      "Faster identification and resolution of issues",
      "Data-driven optimization of gas distribution",
    ],
  },

  {
    id: "U1UCI351",
    image: "/images/16.png",
    title: "Preparation of Estimated Profitability for MIS and Advance Tax",
    team: "ProfitPro AI",
    description:
      "An AI-enabled financial analytics solution that estimates company profitability using revenue trends, cost data, and historical financials to support MIS reporting and advance tax planning.",
    features: [
      "Automated profitability estimation using financial data",
      "Analysis of revenue and cost trends",
      "Scenario-based projections for financial planning",
      "MIS-ready dashboards and reports",
    ],
    whyRequired: [
      "Manual profitability estimation is time-consuming and error-prone",
      "Delayed financial insights affect advance tax planning",
      "Limited visibility into future profitability trends",
      "Heavy reliance on manual consolidation across departments",
    ],
    benefits: [
      "Improved accuracy of advance tax calculations",
      "Reduced manual effort in MIS preparation",
      "Enhanced financial planning and compliance",
      "Timely and reliable profitability insights",
    ],
  },

  {
    id: "U1UCI447A",
    image: "/images/21.png",
    title: "SAP Inventory Optimization: Deduplication, Consolidation and UI",
    team: "Inv_Optima",
    description:
      "A SAP-integrated inventory optimization solution that removes duplicate material records, consolidates inventory data, and provides a clean UI for better planning and control.",
    features: [
      "Identification and removal of duplicate material masters",
      "Consolidation of inventory records across locations",
      "SAP-integrated user interface for planners and stores",
      "Analytics for excess, slow-moving, and obsolete inventory",
    ],
    whyRequired: [
      "Duplicate material codes inflate inventory and procurement costs",
      "Poor visibility into stock positions across locations",
      "Fragmented inventory data leads to inefficient procurement",
      "Manual reconciliation increases effort and errors",
    ],
    benefits: [
      "Reduced inventory holding and procurement costs",
      "Improved accuracy and reliability of SAP inventory data",
      "Enhanced inventory visibility and planning efficiency",
      "Optimized and data-driven procurement decisions",
    ],
  },

  {
    id: "U1UCI359",
    image: "/images/22 (2).png",
    title: "SmartLift – Intelligent Artificial Lift Optimization",
    team: "Pravaah_AI",
    description:
      "An intelligent artificial lift optimization solution that continuously monitors well performance and recommends optimal lift parameters to maximize production and equipment reliability.",
    features: [
      "Continuous monitoring of artificial lift performance",
      "AI-based optimization of lift operating parameters",
      "Early detection of performance degradation and faults",
      "Performance dashboards for monitoring and analysis",
    ],
    whyRequired: [
      "Static lift settings result in sub-optimal well performance",
      "Delayed fault detection increases downtime and maintenance cost",
      "Manual monitoring is not scalable across large well stock",
      "Limited long-term optimization of lift systems",
    ],
    benefits: [
      "Increased production from existing wells",
      "Reduced equipment failures and downtime",
      "Lower operating and maintenance costs",
      "Proactive and data-driven lift management",
    ],
  },
];

const projectImages = [
  "/images/3.png",
  "/images/17.png",
  "/images/7.png",
  "/images/2.png",
  "/images/20.png",
  "/images/13.png",
  "/images/5.png",
  "/images/6.png",
  "/images/1.png",
  "/images/14.png",
  "/images/18.png",

  "/images/7.png",
  "/images/13.png",
  "/images/14.png",
];

// Create pages array with two pages per project
const pages = [
  {
    isCover: true,
    title: "Udbhav 1.0 Projects",
  },
];

projects.forEach((project) => {
  // First page of the project
  pages.push({
    ...project,
    isFirstPage: true,
  });
  // Second page of the project
  pages.push({
    ...project,
    isSecondPage: true,
  });
});

const iconMap = {
  Rocket,
  Building,
  Award,
  Target,
  Users,
  Network,
  Lightbulb,
  CheckCircle,
  Code,
  TrendingUp,
};

const Home = () => {
  const navigate = useNavigate();
  // or authToken
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [allDrafts, setAllDrafts] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const bookRef = useRef(null);
  const [htmlFlipBookWidth, setHtmlFlipBookWidth] = useState(500);
  const [isFlipping, setIsFlipping] = useState(false);
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  // const [selected, setSelected] = useState(timelineMilestones[0]);
  const [selected, setSelected] = useState(null);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const onFlip = (e) => {
    setCurrentPage(e.data);
  };

  const nextQuote = () =>
    setQuoteIndex((prev) => (prev + 1) % dignitaryQuotes.length);
  const prevQuote = () =>
    setQuoteIndex((prev) =>
      prev === 0 ? dignitaryQuotes.length - 1 : prev - 1
    );
  useEffect(() => {
    if (window.innerWidth >= 700) {
      setHtmlFlipBookWidth(500);
    } else if (window.innerWidth < 700 && window.innerWidth >= 400) {
      setHtmlFlipBookWidth(400);
    } else if (window.innerWidth < 400) {
      setHtmlFlipBookWidth(300);
    }
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-out",
    });
  }, []);

  return (
    <div className="min-h-screen">
      <section className="relative text-white py-16 sm:py-28 px-6 bg-black min-h-screen flex items-center mt-1">
        {/* Floating Particles */}
        <div className="absolute top-0 left-0 right-0 h-full opacity-10 bg-black pointer-events-none">
          <div
            className="absolute w-2 h-2 bg-red-500 rounded-full animate-float1 opacity-60"
            style={{ left: "10%", top: "20%" }}
          ></div>
          <div
            className="absolute w-3 h-3 bg-blue-500 rounded-full animate-float2 opacity-60"
            style={{ left: "80%", top: "30%" }}
          ></div>
          <div
            className="absolute w-2 h-2 bg-green-500 rounded-full animate-float3 opacity-60"
            style={{ left: "20%", top: "70%" }}
          ></div>
          <div
            className="absolute w-3 h-3 bg-yellow-500 rounded-full animate-float4 opacity-60"
            style={{ left: "70%", top: "60%" }}
          ></div>
          <div
            className="absolute w-2 h-2 bg-purple-500 rounded-full animate-float5 opacity-60"
            style={{ left: "50%", top: "40%" }}
          ></div>
          <div
            className="absolute w-3 h-3 bg-pink-500 rounded-full animate-float6 opacity-60"
            style={{ left: "30%", top: "50%" }}
          ></div>
        </div>

        {/* Overlay */}
        <div className="absolute top-0 left-0 right-0 h-full opacity-10 bg-black"></div>

        {/* Wrapper */}
        <div className="max-w-7xl mx-auto relative z-10 flex flex-col md:flex-row items-center justify-between gap-12 sm:gap-16 w-full">
          {/* LEFT SIDE */}
          <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left space-y-8 sm:space-y-12">

            {/* Logo + Tagline + Heading grouped together */}
            <div className="flex flex-col">
              <div className="relative w-full max-w-[560px] h-[140px] animate-scaleIn">
                <img
                  src="/images/banner-logo.png"
                  className="absolute inset-0 h-full w-auto object-contain animate-fadeSwap"
                />
                <img
                  src="/images/banner1-logo-hindi.png"
                  className="absolute inset-0 h-full w-auto object-contain animate-fadeSwap2"
                />
              </div>

              <h1 className="text-3xl sm:text-3.5xl md:text-2xl font-bold bg-gradient-to-r from-red-500 via-red-400 to-green-500 bg-clip-text text-transparent animate-slideUp mt-0 ms-11">
                Ignite Ideas, Fuel Innovation!
              </h1>

              <h2
                className="text-4xl sm:text-5xl md:text-7xl font-bold md:text-center animate-fadeIn animate-glowPulse mt-10"
                style={{ animationDelay: "900ms" }}
              >
                AI Hackathon
              </h2>
            </div>

            {/* CTA BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-10 sm:gap-14 justify-center md:justify-start">
              {/* CTA + Date */}
              <div className="flex flex-col gap-2">
                {/* <button
    onClick={() =>
      window.open(
        "https://docs.google.com/forms/d/e/1FAIpQLSekok3wYw-j3eSw0OGBB1amsopP7PzZ4H0NHkQ9WDcFLEPyfA/viewform",
        "_blank",
        "noopener,noreferrer"
      )
    }
    className="bg-red-600 hover:bg-red-700 text-white text-base sm:text-xl font-bold px-7 py-3.5 rounded-full transition-all hover:shadow-2xl sm:hover:scale-110 flex items-center gap-2"
  >
    Submit Use Case Idea →
  </button> */}
                <button
                  onClick={() => navigate("/finalists")}
                  className="w-fit group relative inline-flex items-center gap-2.5 px-5 py-3 sm:px-7 sm:py-3.5 rounded-full text-white font-bold text-sm sm:text-[15px] tracking-wide bg-gradient-to-r from-red-600 to-green-600 hover:opacity-95 transition-all duration-300 hover:-translate-y-px active:translate-y-0 shadow-[0_0_20px_rgba(220,38,38,0.25)] hover:shadow-[0_0_25px_rgba(34,197,94,0.4)] outline-none"
                >
                  {/* <Users className="w-[17px] h-[17px] text-white shrink-0" /> */}
                  <span>Meet the Finalists</span>
                  <ArrowRight className="w-[15px] h-[15px] text-white transition-transform duration-300 group-hover:translate-x-1" />
                </button>

                {/* Live Status Indicator */}
                <div className="w-fit inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full mt-3">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-emerald-400 text-sm sm:text-[17px] font-medium whitespace-normal sm:whitespace-nowrap text-left animate-pulse">
                    Teams are now preparing for Demo Day
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* RIGHT SIDE - 3D ROTATING CAROUSEL */}
          <div
            className="
    relative
    w-[180px] sm:w-[220px] md:w-[45%]
    min-w-0
    h-[200px] sm:h-[250px] md:h-[500px]
    flex
    items-center
    justify-center
    animate-slideInRight
    perspective-container
  "
            style={{ animationDelay: "600ms" }}
          >
            {/* Glowing Background Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 via-purple-500/20 to-blue-500/20 blur-3xl animate-pulse"></div>

            {/* 3D Carousel Container */}
            <div className="carousel-3d">
              {[
                { id: 1, title: "Use Case Presentation" },
                { id: 2, title: "Review Meeting" },
                { id: 3, title: "Udbhav 1.0 Demo Day" },
                { id: 4, title: "Udbhav Launch @IEW 2025" },
                { id: 5, title: "Use Case Idea Submission" },
                { id: 6, title: "Udbhav 1.0 Winners" },

              ].map((item, index) => (
                <div
                  className="carousel-item flex flex-col items-center"
                  style={{ "--i": index + 1 }}
                  key={item.id}
                >
                  {/* Image */}
                  <img
                    src={`/images/reel${item.id}.jpg`}
                    alt={item.title}
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />

                  <p className="mt-3 flex items-center justify-center text-white text-sm sm:text-base text-center font-normal">
                    {item.title}
                  </p>

                </div>
              ))}
            </div>

            {/* Decorative Corner Accents - hidden on mobile */}
            <div className="hidden sm:block absolute -top-2 -right-2 sm:-top-2 sm:-right-4 md:-top-2 md:-right-4 w-20 sm:w-24 h-20 sm:h-24 border-t-4 border-r-4 border-red-500 rounded-tr-3xl z-20"></div>
            <div className="hidden sm:block absolute -bottom-2 -left-2 sm:-bottom-1 sm:-left-4 md:-bottom-1 md:-left-4 w-20 sm:w-24 h-20 sm:h-24 border-b-4 border-l-4 border-green-500 rounded-bl-3xl z-20"></div>
          </div>


        </div>

        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>

        {/* CSS Animations */}
        <style jsx>{`
          /* Logo Swap */
          @keyframes fadeSwap {
            0%,
            45% {
              opacity: 1;
            }
            50%,
            95% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }
          @keyframes fadeSwap2 {
            0%,
            45% {
              opacity: 0;
            }
            50%,
            95% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
          .animate-fadeSwap {
            animation: fadeSwap 8s infinite;
          }
          .animate-fadeSwap2 {
            animation: fadeSwap2 8s infinite;
          }

          /* Glow Text */
          @keyframes glowPulse {
            0%,
            100% {
              text-shadow: 0 0 12px rgba(255, 80, 80, 0.6);
            }
            50% {
              text-shadow: 0 0 25px rgba(255, 60, 60, 1);
            }
          }
          .animate-glowPulse {
            animation: glowPulse 2s infinite;
          }

          /* Fade In */
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(25px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 1s forwards;
          }

          /* Slide */
          @keyframes slideInLeft {
            0% {
              opacity: 0;
              transform: translateX(-50px);
            }
            100% {
              opacity: 1;
              transform: none;
            }
          }
          .animate-slideInLeft {
            animation: slideInLeft 0.8s forwards;
          }

          @keyframes slideInRight {
            0% {
              opacity: 0;
              transform: translateX(50px);
            }
            100% {
              opacity: 1;
              transform: none;
            }
          }
          .animate-slideInRight {
            animation: slideInRight 0.8s forwards;
          }

          @keyframes slideUp {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-slideUp {
            animation: slideUp 1s forwards;
          }

          /* Scale */
          @keyframes scaleIn {
            0% {
              opacity: 0;
              transform: scale(0.8);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
          .animate-scaleIn {
            animation: scaleIn 0.8s forwards;
          }

          /* Floating Particles */
          @keyframes float1 {
            0%,
            100% {
              transform: translate(0, 0);
            }
            50% {
              transform: translate(20px, -30px);
            }
          }
          @keyframes float2 {
            0%,
            100% {
              transform: translate(0, 0);
            }
            50% {
              transform: translate(-15px, 25px);
            }
          }
          @keyframes float3 {
            0%,
            100% {
              transform: translate(0, 0);
            }
            50% {
              transform: translate(25px, 20px);
            }
          }
          @keyframes float4 {
            0%,
            100% {
              transform: translate(0, 0);
            }
            50% {
              transform: translate(-20px, -25px);
            }
          }
          @keyframes float5 {
            0%,
            100% {
              transform: translate(0, 0);
            }
            50% {
              transform: translate(15px, 30px);
            }
          }
          @keyframes float6 {
            0%,
            100% {
              transform: translate(0, 0);
            }
            50% {
              transform: translate(-25px, -20px);
            }
          }
          .animate-float1 {
            animation: float1 8s ease-in-out infinite;
          }
          .animate-float2 {
            animation: float2 10s ease-in-out infinite;
          }
          .animate-float3 {
            animation: float3 7s ease-in-out infinite;
          }
          .animate-float4 {
            animation: float4 9s ease-in-out infinite;
          }
          .animate-float5 {
            animation: float5 11s ease-in-out infinite;
          }
          .animate-float6 {
            animation: float6 6s ease-in-out infinite;
          }

          /* 3D Carousel */
          .perspective-container {
            perspective: 1200px;
          }
          .carousel-3d {
            position: relative;
            width: 220px;
            height: 250px;
            transform-style: preserve-3d;
            animation: rotate3d 20s linear infinite;
          }
          .carousel-3d:hover {
            animation-play-state: paused;
          }
          @keyframes rotate3d {
            0% {
              transform: rotateY(0deg);
            }
            100% {
              transform: rotateY(360deg);
            }
          }

          .carousel-item {
            position: absolute;
            width: 200px;
            height: 230px;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%) rotateY(calc(var(--i) * 60deg))
              translateZ(180px);
            transition: all 0.5s ease;
            opacity: 1;
          }
          .carousel-item:hover {
            transform: translate(-50%, -50%) rotateY(calc(var(--i) * 60deg))
              translateZ(220px);
            opacity: 1;
          }
          .carousel-item img {
            border-radius: 1rem;
            width: 100%;
            height: 100%;
            object-fit: cover;
            border: 4px solid rgba(255, 255, 255, 0.2);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5),
              0 0 40px rgba(255, 59, 48, 0.3);
          }

          /* Desktop-only enhancement */
          @media (min-width: 1024px) {
            .carousel-3d {
              width: 300px;
              height: 340px;
            }
            .carousel-item {
              width: 260px;
              height: 300px;
              transform: translate(-50%, -50%) rotateY(calc(var(--i) * 60deg))
                translateZ(240px);
            }
            .carousel-item:hover {
              transform: translate(-50%, -50%) rotateY(calc(var(--i) * 60deg))
                translateZ(280px);
            }
          }

          /* Mobile adjustments */
          @media (max-width: 639px) {
            .carousel-3d {
              width: 180px;
              height: 200px;
            }
            .carousel-item {
              width: 160px;
              height: 180px;
              transform: translate(-50%, -50%) rotateY(calc(var(--i) * 60deg))
                translateZ(140px);
            }
          }

          /* Small tablets */
          @media (min-width: 640px) and (max-width: 1023px) {
            .carousel-3d {
              width: 220px;
              height: 250px;
            }
            .carousel-item {
              width: 200px;
              height: 230px;
              transform: translate(-50%, -50%) rotateY(calc(var(--i) * 60deg))
                translateZ(180px);
            }
          }
        `}</style>
      </section>

      {/* What's Different Section */}
      <section className="relative pt-10 pb-24 px-6 bg-gradient-to-b from-black via-[#0a0e1a] to-black text-white overflow-hidden">
        {/* Background soft dotted glow */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none bg-[radial-gradient(circle,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[size:18px_18px]"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          {/* --- Confetti Ribbon Section (Moved Up) --- */}
          <section className="relative overflow-hidden w-full rounded-lg mb-16">
            {/* Gradient Ribbon Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-black to-green-600 opacity-90 animate-gradient-move"></div>

            {/* Confetti bomb container */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(30)].map((_, i) => (
                <span
                  key={i}
                  className="confetti absolute w-2 h-2 rounded-sm"
                  style={{
                    left: `${Math.random() * 100}%`,
                    backgroundColor: [
                      "#F87171",
                      "#FACC15",
                      "#4ADE80",
                      "#60A5FA",
                      "#A78BFA",
                      "#FB7185",
                    ][Math.floor(Math.random() * 6)],
                    animationDelay: `${Math.random() * 1.5}s`,
                    animationDuration: `${2 + Math.random() * 3}s`,
                  }}
                ></span>
              ))}
            </div>
            <div className="relative z-10 py-4 text-center">
              <p className="text-base sm:text-lg font-semibold tracking-wide ">
                <span className="text-white">
                  <span className="text-green-400 font-bold">Status Update:</span>{" "}
                  Shortlisted teams are presenting their final outputs | Demo Day scheduled for{" "}
                  <span className="text-green-400 font-bold">
                    7th July (Tentative)
                  </span>
                </span>
              </p>
            </div>


          </section>

          {/* Section Heading */}
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-2xl md:text-4xl font-extrabold mb-6 bg-gradient-to-r from-red-400 via-emerald-400 to-green-500 bg-clip-text text-transparent tracking-tight drop-shadow-[0_0_20px_rgba(0,255,150,0.2)]">
              Phases of Udbhav 2.0
            </h2>
            <div className="text-sm md:text-base text-gray-300 max-w-2xl mx-auto leading-snug space-y-2">
              <p className="line-clamp-2">
                Udbhav 2.0 is is designed to turn ideas into impactful solutions through three focused phases of building, refining, and showcasing AI-driven solutions
              </p>
            </div>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {whatsNew.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card
                  key={index}
                  className="group 
          border border-transparent
          hover:border-emerald-400/40
          bg-gradient-to-br from-[#111827]/70 to-[#1f2937]/70 
          backdrop-blur-xl
          shadow-lg hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]
          transition-all duration-300 rounded-2xl relative overflow-hidden"
                  data-aos="fade-up"
                  data-aos-delay={index * 150}
                >
                  {/* Glow highlight on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-20 bg-gradient-to-r from-emerald-500 to-red-500 transition-opacity duration-300"></div>

                  <CardContent className="p-8 text-center relative z-10">
                    {/* Icon Container */}
                    <div
                      className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-red-600/20 to-green-400/20 
                          rounded-2xl flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    >
                      <Icon className="w-8 h-8 text-emerald-300 drop-shadow-[0_0_5px_rgba(0,255,150,0.6)]" />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-red-300 via-emerald-300 to-green-300 bg-clip-text text-transparent tracking-wide">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-300 leading-relaxed mb-2 text-lg">
                      {item.description}
                    </p>

                    {/* Dates */}
                    {item.dates && item.dates.map((date, idx) => (
                      <p key={idx} className="text-sm leading-relaxed mt-1 bg-gradient-to-r from-red-300 via-emerald-300 to-green-300 bg-clip-text text-transparent">
                        {date}
                      </p>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>
        {/* Animations */}
        <style jsx>{`
          @keyframes slideUp {
            0% {
              opacity: 0;
              transform: translateY(25px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-slideUp {
            animation: slideUp 1s forwards;
          }

          @keyframes textShimmer {
            0% {
              background-position: 0% 50%;
            }
            100% {
              background-position: 200% 50%;
            }
          }
          .animate-textShimmer {
            background-size: 200% auto;
            animation: textShimmer 3s linear infinite;
          }

          @keyframes fall {
            0% {
              transform: translateY(0) rotate(0deg) scale(1);
              opacity: 1;
            }
            100% {
              transform: translateY(150px) rotate(720deg) scale(0.9);
              opacity: 0;
            }
          }
          .confetti {
            animation: fall ease-in-out forwards;
          }

          @keyframes gradient-x {
            0%,
            100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          .animate-gradient-x {
            background-size: 200% 200%;
            animation: gradient-x 6s ease infinite;
          }

          @keyframes gradient-move {
            0%,
            100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          .animate-gradient-move {
            background-size: 200% 200%;
            animation: gradient-move 8s linear infinite;
          }
        `}</style>
      </section>
      <section className="relative bg-black text-white px-8 py-16 overflow-hidden">
        {/* Subtle dotted background */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none 
                bg-[radial-gradient(circle,_rgba(255,255,255,0.08)_1px,_transparent_1px)] 
                bg-[size:20px_20px]"
        />

        {/* SECTION TITLE */}
        <div className="relative z-10 max-w-7xl mx-auto text-center mb-14">
          <h2
            className="text-2xl md:text-4xl font-extrabold bg-gradient-to-r 
                 from-red-500 via-emerald-400 to-green-500 
                 bg-clip-text text-transparent tracking-tight"
          >
            Why Participate ?
          </h2>

          <p className="mx-auto text-base md:text-sm text-gray-300 max-w-2xl leading-relaxed mt-4">
            <span className="text-emerald-400 font-semibold">Udbhav </span>
            gives you a platform to solve real problems, build practical AI solutions, and gain visibility in front of top management of ONGC while contributing to ONGC's digital future.
          </p>
        </div>

        {/* CONTENT - SINGLE ROW LAYOUT */}
        <div className="relative z-10 max-w-7xl mx-auto">
          {/* THREE COLUMNS IN ONE ROW */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* LEFT - BENEFITS */}
            <div className="space-y-4">
              {benefits.slice(0, 1).map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={benefit.id}
                    className="flex items-center gap-4 border border-white/10 
                       rounded-xl px-5 py-4 hover:border-emerald-400/50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>

                    <div className="flex-1">
                      <h3
                        className="text-lg font-bold bg-gradient-to-r 
                             from-green-500 via-red-400 to-green-500 
                             bg-clip-text text-transparent"
                      >
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                );
              })}
              {benefits.slice(1, 2).map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={benefit.id}
                    className="flex items-center gap-4 border border-white/10 
                       rounded-xl px-5 py-4 hover:border-emerald-400/50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>

                    <div className="flex-1">
                      <h3
                        className="text-lg font-bold bg-gradient-to-r 
                             from-green-500 via-red-400 to-green-500 
                             bg-clip-text text-transparent"
                      >
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                );
              })}
              {benefits.slice(2, 3).map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={benefit.id}
                    className="flex items-center gap-4 border border-white/10 
                       rounded-xl px-5 py-4 hover:border-emerald-400/50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>

                    <div className="flex-1">
                      <h3
                        className="text-lg font-bold bg-gradient-to-r 
                             from-green-500 via-red-400 to-green-500 
                             bg-clip-text text-transparent"
                      >
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* MIDDLE - RECOGNITION & EXECUTIVE VISIBILITY */}
            <div
              className="relative border border-emerald-400/30 rounded-2xl
             p-6
             bg-gradient-to-br from-emerald-500/10 via-black to-red-500/10
             backdrop-blur "
            >
              {/* TITLE */}
              <div className="text-center mb-1 -mt-2">
                <h3
                  className="text-xl md:text-2xl font-extrabold
    bg-gradient-to-r from-green-500 to-red-500
    bg-clip-text text-transparent"
                >
                  Recognition & Executive Visibility
                </h3>
              </div>

              <div className="space-y-15 mb-1 mt-9">
                {/* POINT 01 */}
                <div className="flex gap-4">
                  <div className="text-emerald-400 text-lg font-bold flex-shrink-0">01</div>
                  <p className="text-gray-200 text-sm leading-relaxed">
                    All <span className="text-emerald-400 font-semibold">
                      workable Use Case idea submissions
                    </span> will receive a{" "}
                    <span className="text-white font-semibold">
                      Digital Certificate
                    </span> from{" "}
                    <span className="text-emerald-400 font-semibold">
                      ED-CIO, ONGC
                    </span>.
                  </p>
                </div>

                <div className="h-px bg-white/10 my-4" />

                {/* POINT 02 */}
                <div className="flex gap-4">
                  <div className="text-emerald-400 text-lg font-bold flex-shrink-0">02</div>
                  <p className="text-gray-200 text-sm leading-relaxed">
                    All <span className="text-emerald-400 font-semibold">
                      workable Use Case idea submissions
                    </span>, along with{" "}
                    <span className="text-white font-semibold">
                      Udbhav 1.0 ideas
                    </span>, with{" "}
                    <span className="text-emerald-400 font-semibold">
                      full team attribution
                    </span>, will be curated into a{" "}
                    <span className="text-white font-semibold">book</span> and presented to{" "}
                    <span className="text-emerald-400 font-semibold">
                      ONGC's Top Management
                    </span>.
                  </p>
                </div>
              </div>


              <div
                className="absolute bottom-0 left-0 w-full h-[2px]
                  bg-gradient-to-r from-transparent via-red-500
                  to-transparent opacity-60"
              />
            </div>

            {/* Right Column – Important Dates */}
            <div className="relative rounded-2xl px-8 pt-6 pb-10 flex flex-col h-full">
              {/* Vertical Divider */}
              <div className="absolute left-6 top-6 bottom-6 w-1 bg-gradient-to-b from-green-500 to-red-500 rounded-full" />

              <h3 className="text-3xl font-bold mb-6 ml-10 bg-gradient-to-r from-red-500 to-green-500 bg-clip-text text-transparent">
                Important Dates
              </h3>

              <div className="flex flex-col ml-10 text-gray-200 text-sm w-full">
                <div className="pb-4 border-b border-gray-700">
                  <p className="font-semibold text-white">
                    Idea Submission & Evaluation
                  </p>

                  {/* Date with Extended badge in front */}
                  <div className="flex items-center gap-2">
                    <p >09 Jan (Friday) – 18 Jan (Sunday)</p>
                    {/* <span className="bg-yellow-300 text-black text-xs sm:text-sm font-semibold px-2 py-0.5 rounded-sm italic">
                    Extended
                  </span> */}
                  </div>
                </div>

                <div className="py-4 border-b border-gray-700">
                  <p className="font-semibold text-white">Use Case Development</p>
                  <p>11 Feb (Wednesday) - 27 Apr (Monday)</p>
                </div>
                <div className="pt-4">
                  <p className="font-semibold text-white">Finale (Demo Day)</p>
                  <p> 7 July (Tentative)</p>
                </div>
              </div>

              {/* Coming Soon Badge */}
              {/* <div className="mt-8 mx-auto w-fit bg-gradient-to-br from-emerald-500/20 via-red-500/10 to-transparent px-6 py-2 rounded-xl border border-emerald-400/40 shadow-[0_0_15px_rgba(16,185,129,0.3)] backdrop-blur-md animate-pulse-soft">
        <p className="text-emerald-400 font-semibold text-sm flex items-center gap-2">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-ping"></span>
          Coming Soon
        </p>
      </div> */}
            </div>
          </div>
        </div>
      </section>


      {/* 🌟 What is Udbhav Section */}
      {/* 🌟 What is Udbhav Section */}
      {/* 🌟 What is Udbhav Section */}
      {/* 🌟 What is Udbhav Section */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-black via-[#0a0a0a] to-[#111] text-white">
        <div className="max-w-5xl mx-auto text-center" data-aos="fade-up">
          {/* Heading */}
          <h2 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent">
            What is Udbhav ?
          </h2>

          {/* Description */}
          {/* Description */}
          <p className="text-sm md:text-base text-gray-300 leading-relaxed font-medium mb-4">
            <span className="text-emerald-400 font-semibold">Udbhav</span> is
            ONGC's flagship innovation program designed to harness the
            collective intelligence of our workforce. It empowers employees to
            identify innovative solutions for real challenges.
          </p>

          {/* Centered Timeline */}
          <p className="text-sm md:text-base text-gray-300 leading-relaxed font-medium mb-4 text-center">
            Udbhav 1.0 was conducted from Feb 2025 to Aug 2025. Udbhav 2.0 is
            ongoing now.
          </p>


          {/* Quote Box */}
          {/* Quote Box */}
          {/* Quote Box */}
          <div className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm shadow-lg shadow-red-500/10 hover:shadow-red-500/20 transition-all duration-500 text-center">
            <p className="text-gray-300 italic leading-relaxed mb-3">
              "Udbhav represents our commitment to fostering a culture of
              innovation.
              <br />
              Every employee has the potential to be a change maker,
              <br />
              and this platform provides the opportunity to turn ideas into
              reality."
            </p>
            <p className="text-red-400 font-semibold">ED-CIO, ONGC</p>
          </div>

        </div>

        {/* Bottom accent divider */}
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>
      </section>

      {/* <section
  id="journey-section"
  className="py-16 px-6 bg-gradient-to-br from-black via-[#0a0a0a] to-[#101010] text-white relative overflow-hidden"
>
  
  <div className="text-center mb-14">
    <h2 className="text-2xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-red-400 via-green-400 to-emerald-500 bg-clip-text text-transparent">
      The Journey So Far
    </h2>
    <div className="w-28 h-1 bg-emerald-400 mx-auto rounded-full"></div>
  </div>


  <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-start relative z-10">

    <div className="relative pl-12 order-2 md:order-1 z-20">
      <div className="absolute left-2 top-4 bottom-0 w-[2px] bg-emerald-600/25"></div>

      <div className="space-y-12">
        {timelineMilestones.map((milestone) => {
          const Icon = iconMap[milestone.icon];
          const isActive = selected?.id === milestone.id;

          return (
            <div
              key={milestone.id}
              className="relative group cursor-pointer"
              onMouseEnter={() => setSelected(milestone)}
              onMouseLeave={(e) => {
                const related = e.relatedTarget;
                if (!(related instanceof Element) || !related.closest(".hover-popup")) {
                  setSelected(null);
                }
              }}
            >
    
<div
  className={`absolute -left-6 top-1 w-12 h-12 rounded-full flex items-center justify-center 
  shadow-[0_0_18px_rgba(6,95,70,0.35)] transition-transform duration-300 z-30 ${
    isActive ? "scale-110" : "scale-100"
  }`}
  style={{
    background:
      "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.95), rgba(6,95,70,0.7))",
    border: "3px solid rgba(16,185,129,0.25)",
  }}
>
  <Icon size={40} className="text-emerald-200 drop-shadow-md" />
</div>


        
              <div className="pl-8">
                <h3
                  className={`text-xl font-semibold mb-1 ${
                    isActive ? "text-emerald-300" : "text-white"
                  }`}
                >
                  {milestone.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {milestone.description}
                </p>
              </div>

{isActive && (
  <div
    className={`hover-popup z-[999]
      bg-white/10 backdrop-blur-md border border-emerald-400/30
      text-gray-100 p-5 rounded-xl
      shadow-[0_0_30px_rgba(16,185,129,0.4)]
      animate-fade-in-up


      md:absolute md:left-[70%] md:top-0 md:mt-2 md:w-80

      relative mt-4 w-full
    `}
  >
    <h4 className="text-lg font-semibold mb-2 text-emerald-300">
      {milestone.title}
    </h4>
    <p className="text-gray-300 text-sm leading-relaxed">
      {milestone.details}
    </p>
  </div>
)}

            </div>
          );
        })}
      </div>
    </div>


<div className="relative w-full flex flex-col items-center justify-center order-1 md:order-2">

  <h3 className="text-3xl md:text-4xl font-bold text-center mb-10">
    <span className="bg-gradient-to-r from-red-400 via-red-400 to-emerald-400 bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(16,185,129,0.4)]">
      Udbhav
    </span>{" "}
    <span className="text-emerald-400">Journey Showreel</span>
  </h3>


  <div className="flex flex-col items-center justify-center my-12">


    <div className="relative rounded-2xl shadow-[0_0_25px_rgba(16,185,129,0.4)] border border-emerald-600/40 overflow-hidden">
      <video
        className="rounded-2xl w-[750px] md:w-[900px] lg:w-[1000px] border border-emerald-700/40"
        src="/vedios/Final Showreel Video.mp4"
        autoPlay
        muted
        loop
        playsInline
        controls
      />
    </div>


    <p className="text-gray-300 text-lg italic text-center mt-12">
      From idea to Demo Day —{" "}
      <span className="text-emerald-400 font-medium">
        relive the journey.
      </span>
    </p>

  </div>
</div>


  </div>


  <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none"></div>


  <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>

  <style jsx>{`
    @keyframes fade-in-up {
      0% {
        opacity: 0;
        transform: translateY(6px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fade-in-up {
      animation: fade-in-up 0.3s ease-out forwards;
    }
  `}</style>
</section> */}

      {/* Introducing Udbhav 2.0 Section */}
      {/* <section className="relative py-28 px-6 bg-gradient-to-b from-black via-[#0b0d10] to-[#050505] text-white overflow-hidden">


  <div className="absolute inset-0 flex items-center justify-center">
    <div className="w-[550px] h-[550px] bg-gradient-to-r from-red-500/25 via-emerald-400/15 to-green-400/20 blur-3xl rounded-full animate-pulse" />
  </div>

  <div className="max-w-5xl mx-auto text-center relative z-10" data-aos="fade-up">

    <h2 className="text-5xl md:text-6xl font-extrabold mb-10 bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent animate-gradient-x drop-shadow-[0_0_15px_rgba(16,185,129,0.3)]">
      Introducing Udbhav 2.0
    </h2>

  
    <div className="relative group bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl p-10 shadow-2xl shadow-emerald-500/10 transition-transform transform hover:scale-[1.02] hover:shadow-emerald-500/20 overflow-hidden">

      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-700" />

      <div className="relative z-10">
        <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-10">
          <span className="font-semibold text-white">Udbhav 2.0</span> builds on the success of 
          <span className="text-white font-semibold"> Udbhav 1.0</span> — expanding its scope, deepening its impact,
          and moving from ideas to sustained, scalable AI adoption across ONGC.
          <br /><br />
          It reinforces ONGC’s commitment to <span className="text-emerald-400 font-semibold">innovation</span>, 
          <span className="text-red-400 font-semibold"> capability-building</span>, and 
          <span className="text-green-400 font-semibold"> digital transformation</span>.
        </p>

 
        <ul className="text-left text-gray-100 text-lg space-y-5 font-medium mx-auto inline-block">
          <li className="flex items-center gap-4">
            <div className="w-4 h-4 bg-gradient-to-r from-red-400 to-emerald-400 rounded-full shadow-[0_0_12px_#ef4444] animate-ping-slow" />
            <span className="hover:text-emerald-300 transition-colors duration-300">Bigger Challenge</span>
          </li>
          <li className="flex items-center gap-4">
            <div className="w-4 h-4 bg-gradient-to-r from-red-400 to-green-300 rounded-full shadow-[0_0_12px_#10b981] animate-ping-slow" />
            <span className="hover:text-emerald-300 transition-colors duration-300">Deeper AI Adoption</span>
          </li>
          <li className="flex items-center gap-4">
            <div className="w-4 h-4 bg-gradient-to-r from-emerald-400 to-red-400 rounded-full shadow-[0_0_12px_#f87171] animate-ping-slow" />
            <span className="hover:text-emerald-300 transition-colors duration-300">Stronger Outcomes</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section> */}

      {/* <section
  id="journey-section"
  className="py-16 px-6 bg-gradient-to-br from-black via-[#0a0a0a] to-[#101010] text-white relative overflow-hidden"
>

  <div className="text-center mb-14">
    <h2 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-red-400 via-green-400 to-emerald-500 bg-clip-text text-transparent">
      The Journey So Far
    </h2>
    <div className="w-28 h-1 bg-emerald-400 mx-auto rounded-full"></div>
  </div>

  <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-start relative z-10">

    <div className="relative pl-12 order-2 md:order-1 z-20">

      <div className="absolute left-2 top-4 bottom-0 w-[2px] bg-emerald-600/25"></div>

      <div className="space-y-12">
        {timelineMilestones.map((milestone) => {
          const Icon = iconMap[milestone.icon];
          const isActive = selected?.id === milestone.id;

          return (
            <div
              key={milestone.id}
              className="relative group cursor-pointer"
              onMouseEnter={() => setSelected(milestone)}
              onMouseLeave={(e) => {
                const related = e.relatedTarget;
                if (!(related instanceof Element) || !related.closest(".hover-popup")) {
                  setSelected(null);
                }
              }}
            >
      
              <div
                className={`absolute -left-4 top-1 w-8 h-8 rounded-full flex items-center justify-center shadow-[0_0_14px_rgba(6,95,70,0.25)] transition-transform duration-300 z-30 ${
                  isActive ? "scale-110" : "scale-100"
                }`}
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, rgba(16,185,129,0.9), rgba(6,95,70,0.6))",
                  border: "2px solid rgba(16,185,129,0.2)",
                }}
              >
                <Icon size={14} className="text-emerald-200" />
              </div>

              <div className="pl-8">
                <h3
                  className={`text-xl font-semibold mb-1 transition-colors duration-200 ${
                    isActive ? "text-emerald-300" : "text-white"
                  }`}
                >
                  {milestone.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {milestone.description}
                </p>
              </div>

    
{isActive && (
  <div
    className={`hover-popup absolute left-[70%] -translate-x-2 bg-white/10 backdrop-blur-md border border-emerald-400/30 text-gray-100 p-5 rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.4)] w-80 animate-fade-in-up z-[999]
      ${milestone.id === timelineMilestones[timelineMilestones.length - 1].id ? "bottom-full mb-3" : "top-0 mt-2"}`}
  >


                  <h4 className="text-lg font-semibold mb-2 text-emerald-300">
                    {milestone.title}
                  </h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {milestone.details}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>


<div className="relative w-full flex flex-col items-center justify-center order-1 md:order-2 space-y-5">

  <h3 className="text-3xl md:text-4xl font-bold text-center">
    <span className="bg-gradient-to-r from-red-400 via-pink-400 to-emerald-400 bg-clip-text text-transparent drop-shadow-[0_0_6px_rgba(16,185,129,0.4)]">
      Udbhav
    </span>{" "}
    <span className="text-emerald-400">Journey Showreel</span>
  </h3>


  <div className="relative rounded-2xl shadow-[0_0_25px_rgba(16,185,129,0.4)] border border-emerald-600/40 overflow-hidden">
    <video
      className="rounded-2xl w-[750px] md:w-[900px] lg:w-[1000px] border border-emerald-700/40"
      src="/vedios/Final Showreel Video.mp4"
      autoPlay
      muted
      loop
      playsInline
      controls
    ></video>
  </div>

  <p className="text-gray-300 text-lg italic text-center mt-2">
    From idea to Demo Day —{" "}
    <span className="text-emerald-400 font-medium">relive the journey.</span>
  </p>
</div>

  </div>


  <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none"></div>

  <style jsx>{`
    @keyframes fade-in-up {
      0% {
        opacity: 0;
        transform: translateY(6px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fade-in-up {
      animation: fade-in-up 0.3s ease-out forwards;
    }
  `}</style>
</section> */}

      {/* <section
      id="testimonials"
      className="bg-black via-[#0a0a0a] to-[#101010] text-white py-24 px-6 relative overflow-hidden"
    >

      <div className="max-w-5xl mx-auto text-center mb-20">
<h2
  className="text-4xl md:text-5xl font-bold mb-6 pb-1 leading-normal bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent"
  style={{
    WebkitTextFillColor: "transparent",
    WebkitBackgroundClip: "text",
  }}
>
  Messages from ONGC Management
</h2>

        <div className="w-28 h-1 bg-gradient-to-r from-red-400 to-green-400 mx-auto rounded-full mb-10"></div>

        <div className="relative flex items-center justify-center">
          <button
            onClick={prevQuote}
            className="absolute left-0 md:-left-20 bg-black/50 hover:bg-gradient-to-br from-red-500/40 to-green-500/40 border border-emerald-500/20 p-3 rounded-lg transition-all"
          >
            <ChevronLeft className="text-emerald-300" />
          </button>

          <div className="bg-gradient-to-br from-[#0f0f0f] to-[#151515] p-10 rounded-2xl shadow-[0_0_25px_rgba(16,185,129,0.3)] max-w-3xl mx-auto">
            <Quote className="mx-auto mb-4 text-emerald-400 w-8 h-8 opacity-70" />
            <p className="text-xl italic leading-relaxed text-gray-200">
              "{dignitaryQuotes[quoteIndex]}"
            </p>
          </div>

          <button
            onClick={nextQuote}
            className="absolute right-0 md:-right-20 bg-black/50 hover:bg-gradient-to-br from-green-500/40 to-red-500/40 border border-emerald-500/20 p-3 rounded-lg transition-all"
          >
            <ChevronRight className="text-emerald-300" />
          </button>
        </div>
      </div>

    </section> */}

      {/* <section className="relative py-24 px-6 bg-black text-center text-white overflow-hidden">

  <div className="absolute inset-0 -z-10 overflow-hidden">
    <div className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-gradient-to-r from-green-400/10 via-emerald-500/15 to-red-400/10 blur-[160px] animate-glow-pulse" />
    <div className="absolute bottom-[10%] right-[10%] w-[300px] h-[300px] bg-emerald-400/5 blur-[100px] rounded-full animate-blink" />
  </div>

  <div className="max-w-6xl mx-auto relative z-10 flex flex-col items-center">

    <h2 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-green-500 via-emerald-400 to-green-500 bg-clip-text text-transparent ">
      AIX Platform
      
    </h2>


    <p className="text-lg text-gray-300 mb-6 italic">
      Platform where Udbhav solutions{" "}
      <span className="text-emerald-400">continue, grow,</span> and{" "}
      <span className="text-red-400">scale.</span>
    </p>


    <p className="text-gray-400 max-w-3xl text-center mb-12 leading-relaxed">
      AIX hosts the AI applications built under{" "}
      <span className="text-emerald-400 font-medium">Udbhav</span> and provides a
      common structure, secure server space, and long-term deployment framework — 
      enabling teams to move beyond prototypes and keep their solutions active and impactful.
    </p>


    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 justify-center items-center max-w-4xl mx-auto">
      {[
        "Applications built in Udbhav",
        "Structured development guidelines",
        "Dedicated server space",
      ].map((item, index) => (
        <div
          key={index}
          className="relative p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm transition-all duration-700
                     hover:bg-white/10 hover:-translate-y-2 hover:shadow-[0_0_25px_rgba(52,211,153,0.4)]
                     animate-float-slight flex justify-center items-center"
          style={{ animationDelay: `${index * 0.8}s` }}
        >
  
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-transparent rounded-2xl animate-blink-slow opacity-50" />
          <p className="text-gray-200 font-medium text-center relative z-10">
            {item}
          </p>
        </div>
      ))}
    </div>

 
     <div className="mt-14">
      <a
        href="/aix-platform"
        className="inline-block bg-gradient-to-r from-emerald-500 via-green-400 to-red-500 text-white font-semibold text-lg rounded-full px-10 py-4 
                   hover:scale-110 hover:shadow-[0_0_25px_rgba(52,211,153,0.4)] transition-transform duration-300 relative overflow-hidden"
      >
        <span className="relative z-10">Explore AIX Platform</span>
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 animate-button-shine"></span>
      </a>
    </div> 
  </div>

  <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>
  <style>{`
    @keyframes glow-pulse {
      0%, 100% { opacity: 0.6; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.05); }
    }
    .animate-glow-pulse { animation: glow-pulse 6s ease-in-out infinite; }

    @keyframes blink {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
    .animate-blink { animation: blink 5s ease-in-out infinite; }

    @keyframes blink-slow {
      0%, 100% { opacity: 0.2; }
      50% { opacity: 0.5; }
    }
    .animate-blink-slow { animation: blink-slow 6s ease-in-out infinite; }

    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    .animate-shimmer { background-size: 300% auto; animation: shimmer 6s linear infinite; }

    @keyframes float-slight {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-5px); }
    }
    .animate-float-slight { animation: float-slight 5s ease-in-out infinite; }

    @keyframes heading-glow {
      0%, 100% { opacity: 0; transform: translateX(-50%); }
      50% { opacity: 0.6; transform: translateX(50%); }
    }
    .animate-heading-glow { animation: heading-glow 4s linear infinite; }

    @keyframes button-shine {
      0% { background-position: -200% center; opacity: 0; }
      50% { opacity: 0.8; }
      100% { background-position: 200% center; opacity: 0; }
    }
    .animate-button-shine { background-size: 300% auto; animation: button-shine 5s linear infinite; }
  `}</style>
</section> */}

      {/* Alumni Program Highlight */}
      {/* <section className="py-20 px-6 bg-black text-white relative overflow-hidden">
  <div className="max-w-6xl mx-auto text-center">
 
    <h2 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-green-500 via-emerald-400 to-green-500 bg-clip-text text-transparent" data-aos="fade-up">
      Join the Udbhav Alumni Network
    </h2>

    <p
      className="text-lg sm:text-xl mb-12 max-w-3xl mx-auto text-gray-300 leading-relaxed"
      data-aos="fade-up"
      data-aos-delay="100"
    >
      Become part of an exclusive community of innovators driving ONGC’s transformation.
    </p>

   
    <div
      className="grid md:grid-cols-4 gap-8 mb-12"
      data-aos="fade-up"
      data-aos-delay="200"
    >
      {[
        "Location-based alumni chapters",
        "Annual Alumni Meet at HQ",
        "Mentorship opportunities",
        "Exclusive learning Platform ",
      ].map((benefit, index) => (
        <div
          key={index}
          className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-red-500/20 hover:border-red-500/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-red-500/20"
        >
          <p className="text-gray-200 font-medium text-base">{benefit}</p>
        </div>
      ))}
    </div>


    <div
      className="flex flex-wrap gap-4 justify-center"
      data-aos="fade-up"
      data-aos-delay="300"
    >
      <Button
        onClick={() => navigate("/alumni")}
        size="lg"
        className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-full font-semibold transition-all duration-300 hover:scale-[1.03] shadow-md hover:shadow-red-500/30"
      >
        Explore Alumni Platform 
      </Button>

      <Button
        onClick={() => navigate("/knowledge-center")}
        size="lg"
        variant="outline"
        className="border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-lg px-8 py-6 rounded-full font-semibold transition-all duration-300 hover:scale-[1.03]"
      >
        Knowledge Center
      </Button>
    </div>
  </div>

  <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>
</section> */}

      <section className="relative w-full bg-black py-6 sm:py-10 px-2 sm:px-6 text-gray-100">
        <h1 className="text-2xl md:text-4xl font-bold text-center bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent mb-2">
          Past Successful ("Winners") Use Case Ideas of Udbhav 1.0
        </h1>
        <p className="text-center text-white text-2xl md:text-xl mb-8 sm:mb-12 italic">
          For Your Inspiration
        </p>
        {/* ✅ Flipbook wrapper */}
        <div className="relative flex flex-col sm:flex-row justify-center items-center mt-4 sm:mt-0 overflow-hidden px-2 sm:px-0">
          {/* ✨ Left overlay text (hidden on mobile) */}
          <div
            className="hidden md:flex absolute left-[calc(50%-500px)] top-1/2 -translate-y-1/2 
             w-[500px] h-[450px] px-10 py-8 flex-col justify-center 
             text-gray-200 text-[14px] leading-relaxed z-10 pointer-events-none 
             text-left rounded-xl shadow-xl backdrop-blur-md border border-gray-700/40"
          >
            <p className="font-bold text-[15px] text-white mb-6 tracking-wide uppercase">
              20 Ideas · One Journey · Real Impact
            </p>
            <div className="h-[2px] w-20 bg-gradient-to-r from-red-400 to-emerald-400 mb-6" />
            <p className="mb-4">
              Take a few minutes to explore how ideas from
              <span className="font-semibold text-red-300"> Udbhav 1.0 </span>
              evolved into working solutions.
            </p>
            <p className="mb-4">
              Each page reflects the effort, learning, and innovation driven by
              <span className="text-emerald-300 font-semibold">
                {" "}
                ONGC teams
              </span>
              .
            </p>
          </div>

          {/* 📖 Book spine */}
          <div
            className={`pointer-events-none absolute top-1/2 left-1/2
      -translate-x-1/2 -translate-y-1/2
      h-[440px] w-[3px]
      bg-gradient-to-b from-transparent via-gray-500/70 to-transparent
      transition-opacity duration-300
      ${isFlipping ? "opacity-0" : "opacity-100"}
      z-10`}
          />

          {/* 📘 Flipbook */}
          <HTMLFlipBook
            width={htmlFlipBookWidth}
            height={450}
            showCover
            drawShadow
            flippingTime={800}
            className="shadow-2xl relative z-20 
                 w-full sm:w-[500px] 
                 h-auto min-h-[250px] sm:h-[450px] 
                 mx-auto"
            onFlip={() => {
              setIsFlipping(true);
              setTimeout(() => setIsFlipping(false), 800);
            }}
          >
            {pages.map((p, idx) => (
              <div
                key={idx}
                className={`page relative ${p.isCover
                  ? "bg-gradient-to-br from-red-900 via-red-800 to-black text-white"
                  : "bg-white text-gray-900"
                  } shadow-xl flex flex-col overflow-hidden h-auto min-h-[250px] sm:min-h-[450px]`}
              >
                {/* COVER */}
                {p.isCover ? (
                  <div className="w-full h-full p-6 sm:p-10 flex flex-col text-center relative">
                    <img
                      src="/images/oldlogo.png"
                      className="absolute top-2 left-4 h-10"
                    />
                    <img
                      src="/images/logo.png"
                      className="absolute top-2 right-4 h-8"
                    />
                    <div className="flex-1 flex items-center justify-center">
                      <img
                        src="/images/undefined.jfif"
                        className="w-70 h-70 object-contain"
                      />
                    </div>
                    <div className="text-xs text-gray-300 space-y-1">
                      <div>{p.title}</div>
                      <div>Oil and Natural Gas Corporation Limited</div>
                    </div>
                  </div>
                ) : p.isFirstPage ? (
                  <div className="w-full h-[450px] bg-white">
                    <div className="p-2 sm:p-4 bg-[#f2f2f2] h-full flex flex-col">
                      {/* WHITE CONTENT (FIXED SPACE) */}
                      <div
                        className="relative bg-white p-3 sm:p-3 border rounded-md 
                    flex flex-col items-start text-left
                    h-[390px] overflow-hidden"
                      >
                        {/* ID Badge */}
                        <div
                          className="bg-red-700 text-white px-2 py-1 
                      -mx-3 -mt-3 text-[10px] font-bold text-center"
                        >
                          {p.id}
                        </div>

                        {/* Title */}
                        <div className="border-l-4 border-red-600 pl-2 mt-2 w-full">
                          <h3 className="text-[11px] sm:text-[14px] font-bold leading-snug line-clamp-3">
                            {p.title}
                          </h3>
                          <p className="text-[9px] sm:text-[10px] text-gray-600 italic leading-tight mt-1">
                            {p.subtitle}
                          </p>
                        </div>

                        {/* Team */}
                        <div className="mt-2 w-full">
                          <h4 className="text-[9px] sm:text-[10px] font-bold text-red-700 uppercase mb-1">
                            Team
                          </h4>
                          <p className="text-[9px] sm:text-[10px] bg-red-50 p-2 border-l-4 border-red-500 leading-tight">
                            {p.team}
                          </p>
                        </div>

                        {/* Description + Features */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 w-full">
                          <div className="relative bg-[#f3f3f3] border p-2 pt-5 text-[9px] sm:text-[10px] leading-snug">
                            <span className="absolute top-0 left-0 bg-red-600 text-white text-[8px] px-2 py-0.5 font-bold">
                              Description
                            </span>
                            {p.description}
                          </div>

                          <div className="relative bg-[#f3f3f3] border p-2 pt-5 text-[9px] sm:text-[10px] leading-snug">
                            <span className="absolute top-0 left-0 bg-red-600 text-white text-[8px] px-2 py-0.5 font-bold">
                              Key Features
                            </span>
                            <ul className="space-y-1">
                              {p.features.map((f, i) => (
                                <li key={i}>• {f}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* FOOTER – FIXED HEIGHT */}
                      <div
                        className="h-[28px] mt-4 border-t 
                    flex items-center justify-center
                    text-[9px] sm:text-[10px] text-gray-500"
                      >
                        {idx}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 sm:p-6 bg-[#f2f2f2] h-full flex flex-col">
                    <div className="relative bg-white p-2 sm:p-4 border rounded-md text-[7px] sm:text-[8px]">
                      <span className="absolute -top-2 sm:-top-3 left-0 bg-red-600 text-white text-[6px] sm:text-[9px] font-bold px-1 py-[1px] uppercase shadow">
                        Benefits & Impact
                      </span>
                      <div className="space-y-1 mt-2">
                        {p.benefits.map((b, i) => (
                          <p
                            key={i}
                            className="bg-red-50 border-l-4 border-red-500 p-1 leading-tight"
                          >
                            {b}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 flex items-center justify-center my-2 sm:my-4 min-h-0">
                      <img
                        src={p.image}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="mt-4 text-[8px] sm:text-[10px] text-center text-gray-500 border-t  sm:pt-2 h-6 flex items-center justify-center">
                      {idx}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </HTMLFlipBook>
        </div>

        {/* ✨ Blink message BELOW the book */}
        <div className="mt-2 sm:mt-6 flex justify-center">
          <p className="text-[13px] font-medium tracking-wide text-gray-300 flex items-center gap-2">
            {/* <span className="blink text-red-400">&lt;&lt;</span> */}
            <span className="italic">
              Click on the flipbook above to flip pages
            </span>
            {/* <span className="blink text-red-400">&gt;&gt;</span> */}
          </p>
        </div>

        {/* Inline style for blink animation */}
        <style jsx>{`
          @keyframes blink {
            25%,
            75% {
              opacity: 0;
            }
          }
          .blink {
            animation: blink 1s infinite;
          }
        `}</style>
        {/* 🔴 Bottom accent */}
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70" />
      </section>

      {/* <section className="pt-10 pb-16 px-6 bg-black text-white relative overflow-hidden">
  
  <div className="max-w-7xl mx-auto relative z-10">

<div className="mb-16 text-center">


  <h3 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent">
      Event  Resources
  </h3>
</div>



 
    <div className="mb-24">
      <h4 className="text-green-500 text-sm font-semibold mb-10 uppercase tracking-wider">
        SAS Training Videos
      </h4>
      <div className="grid md:grid-cols-2 gap-10 items-start">
     
        <div className="relative rounded-lg overflow-hidden shadow-2xl">
          <iframe
            className="w-full h-[280px] md:h-[320px]"
            src="https://www.youtube.com/embed/XVrSiP8NXbM?si=BxR2Ozo2Ex12LAXu"
            title="Building Advanced Dashboards"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>

      
        <div>
          <h5 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
            Building Advanced Dashboards Using SAS Visual Analytics
          </h5>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
            Covers building advanced dashboards with SAS Visual Analytics features like table joins, parameters, prompts, formatting, page hiding, and tips for exploring reports.
          </p>
          <Button
            onClick={() => navigate("/knowledge-center")}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-6 py-3 rounded-lg transition-all duration-200"
          >
            See More
          </Button>
        </div>
      </div>
    </div>

    
  <div className="mb-24">
      <h4 className="text-green-500 text-sm font-semibold mb-10 uppercase tracking-wider">
        Sample Use Case Ideas
      </h4>
      <div className="grid md:grid-cols-[1fr_280px] gap-10 items-center">
    
        <div>
          <h5 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
            AI-Based Well-Integrity Monitoring Shows Promise
          </h5>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
            AI and ML are optimizing artificial lift systems, such as gas lift and ESPs, by automating adjustments in real-time. This enhances production efficiency, reduces labor costs, and minimizes downtime, leading to significant production gains without manual intervention.
          </p>
          <Button
            onClick={() => navigate("/knowledge-center")}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-6 py-3 rounded-lg transition-all duration-200"
          >
            See More
          </Button>
        </div>

  
        <div className="flex justify-center">
          <div className="relative w-56 h-56 md:w-64 md:h-64">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/40 via-blue-500/40 to-purple-700/40 rounded-full blur-3xl"></div>
            <img 
              src="/images/b-2.png" 
              alt="AI Well Monitoring"
              className="relative w-full h-full object-contain drop-shadow-[0_0_40px_rgba(139,92,246,0.9)]"
            />
          </div>
        </div>
      </div>
    </div> 


    <div>
      <h4 className="text-green-500 text-sm font-semibold mb-10 uppercase tracking-wider">
        AI Training Videos
      </h4>
      <div className="grid md:grid-cols-2 gap-10 items-start">
     
        <div className="relative rounded-lg overflow-hidden shadow-2xl">
          <iframe
            className="w-full h-[280px] md:h-[320px]"
            src="https://www.youtube.com/embed/3573AtnmYc0?si=WIXC8-MjUxFYsPjK"
            title="State of AI in Petroleum"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>


        <div>
          <h5 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
            State of Artificial Intelligence in Petroleum Industry
          </h5>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
            This video discusses the application of AI in the petroleum sector, providing insights into how AI is transforming various aspects of the industry. It's particularly relevant for your team to understand industry-specific applications.
          </p>
          <Button
            onClick={() => navigate("/knowledge-center")}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-6 py-3 rounded-lg transition-all duration-200"
          >
            See More
          </Button>
        </div>
      </div>
    </div>
  </div>


  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50"></div>
</section>   */}

      {/* <section className="py-20 px-6 bg-black text-white relative overflow-hidden">
  <div className="max-w-7xl mx-auto relative z-10">

<div className="max-w-7xl mx-auto relative z-10 text-center">

  <div className="mb-12" data-aos="fade-up">

    <h3 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent">
      Rules of the Hackathon
    </h3>
  </div>
</div>


    <div data-aos="fade-up" data-aos-delay="100">
      <ol className="space-y-6 list-decimal list-inside">
        <li className="text-white leading-relaxed text-base md:text-lg">
         You may submit an idea individually or as a team. Having a mentor is encouraged for better guidance.
        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
          If you are looking to collaborate or form a team, you may reach out to us at{' '}
          <a
            href="mailto:udbhav@oges.co"
            className="text-blue-400 hover:text-blue-300 underline transition-colors"
          >
            udbhav@oges.co
          </a>{' '}
          or{' '}
          <a
            href="mailto:vyas_rishi@ongc.co.in"
            className="text-blue-400 hover:text-blue-300 underline transition-colors"
          >
            vyas_rishi@ongc.co.in
          </a>{' '}
          and we will try to support you wherever possible.
        </li>


        <li className="text-white leading-relaxed text-base md:text-lg">
          Team composition can be modified in the early stages, subject to review by the organizing team
        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
          Ideas will be assessed based on clarity, relevance, feasibility, innovation, and potential business impact — not just technical depth.
        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
         Shortlisted ideas may go through mentorship, refinement, reviews, and demos as part of the Udbhav journey.
        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
         Certain stages (such as demo days or reviews) may be conducted online or in person, depending on program requirements.
        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
           Participants are expected to submit original ideas. Plagiarism or copied submissions are strictly discouraged.
        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
         Each submission will be evaluated independently, whether submitted by an individual or a team.

        </li>

        <li className="text-white leading-relaxed text-base md:text-lg">
         If a participant or team submits more than one idea, each idea will be treated as a separate submission.
        </li>
           <li className="text-white leading-relaxed text-base md:text-lg">
         By participating in Udbhav, you agree to abide by the program’s Terms & Conditions and evaluation guidelines.
        </li>
           <li className="text-white leading-relaxed text-base md:text-lg">
        The intellectual property (IP) of the final solution developed under Udbhav will belong to ONGC.


        </li>
      </ol>
    </div>
  </div>  <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div> <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-70"></div>
</section> */}

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-black text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="mb-12 text-center" data-aos="fade-up">
            <h3 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-red-500 via-emerald-400 to-green-500 bg-clip-text text-transparent">
              Frequently Asked Questions
            </h3>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4" data-aos="fade-up" data-aos-delay="100">
            {[
              {
                que: "Where can I view details of Udbhav 1.0?",
                ans: 'You can explore all highlights, projects, and outcomes of Udbhav 1.0 on the dedicated Previous Edition page . <a href="/udbhav-awards" class="text-red-400 hover:text-red-300 underline">Click Here</a>',
              },
              {
                que: "Where can I view all submitted ideas?",
                ans: 'Explore all submitted ideas on the Udbhav 1.0 website. <a href="/udbhav-case" class="text-red-400 hover:text-red-300 underline">Click Here</a>.',
              },

              {
                que: "Why is Udbhav being organized?",
                ans: "Udbhav is ONGC’s platform to surface practical, high impact ideas from within the organization and convert them into real, deployable AI enabled solutions. It encourages experimentation, collaboration, and hands on innovation beyond routine roles.",
              },
              {
                que: "Where can I see team details for Udbhav 1.0 implemented use cases?",
                ans: 'Team member details are available on the Udbhav 1.0 website. <a href="https://udbhav.ogesone.com/winners" target="_blank" class="text-red-400 hover:text-red-300 underline">Click Here</a>.',
              },
              {
                que: "Who can submit an idea?",
                ans: "Any ONGC employee with an idea that can create business or operational value. You don’t need to be from a technical background—domain knowledge and problem understanding are equally important.",
              },
              {
                que: "Can I submit an idea alone, or do I need a team?",
                ans: "You can submit an idea individually or as a team. Having a mentor or team members is recommended for better execution, but it is not mandatory at the idea submission stage.",
              },
              {
                que: "Do I need strong coding skills to participate?",
                ans: "Not necessarily. Successful teams often combine domain expertise, problem clarity, and basic technical capability. However, as ideas move forward, teams are expected to demonstrate working logic or prototypes with appropriate technical support.",
              },
              {
                que: "What happens after I submit my idea?",
                ans: "All submissions are reviewed by the evaluation committee. Shortlisted ideas move forward to the next phase, where teams refine their concept, build a working solution, and receive structured guidance.",
              },
              {
                que: "What kind of support is provided during development?",
                ans: "Shortlisted teams receive mentoring, periodic reviews, and technical guidance. The focus is on helping teams convert ideas into functional, scalable solutions not just presentations.",
              },
              {
                que: "How much time commitment is expected?",
                ans: "Udbhav runs alongside regular work responsibilities. While flexibility is provided, teams are expected to actively participate during review cycles, mentoring sessions, and development milestones.",
              },
              {
                que: "What are ideas evaluated on?",
                ans: "Ideas are assessed on multiple dimensions, including:Business or operational valuePractical applicability and scalabilityUse of AI/data driven approachesNovelty and clarity of the solution Progress towards a working solution.",
              },
              {
                que: "What happens during the Demo Day?",
                ans: "Shortlisted teams may be required to travel to the designated location for Demo Day. Details will be communicated well in advance.",
              },
              {
                que: "What happens after Udbhav concludes?",
                ans: "Promising solutions may move toward implementation, scaling, or further development. Contributors also become part of the Udbhav Alumni Program, enabling continued learning and collaboration.",
              },
              {
                que: "Who owns the idea or solution developed?",
                ans: "All solutions developed under Udbhav are owned by ONGC. Contributors may be invited to participate further in refining or implementing their solutions.",
              },
              {
                que: "I have a question that’s not covered here. Who should I contact?",
                ans: "You can reach out to the Udbhav team at udbhav@oges.co or vyas_rishi@ongc.co.in for any queries or clarifications.",
              },
              {
                que: "Can I submit more than one idea?",
                ans: "Yes. You may submit more than one idea. Each submission will be evaluated independently based on its merit, feasibility, and potential impact.",
              },
              {
                que: "Can I modify my idea after submission?",
                ans: "Minor refinements may be allowed during early stages, especially if suggested by mentors or reviewers. Any major changes will be subject to review by the organizing team.",
              },
              {
                que: "What if my idea is shortlisted but I face constraints later?",
                ans: "Udbhav is designed to be flexible. If genuine constraints arise, teams are encouraged to communicate early so suitable adjustments or guidance can be provided.",
              },
              {
                que: "Will mentorship be available throughout the journey?",
                ans: "Yes. Shortlisted teams will receive structured mentorship during key phases, including idea refinement, development, and preparation for reviews or demos.",
              },
              {
                que: "What kind of ideas are encouraged in Udbhav 2.0?",
                ans: "Udbhav 2.0 encourages ideas that are practical, scalable, and aligned with real operational or business challenges. Solutions should aim for measurable impact rather than theoretical concepts.",
              },
              {
                que: "Is it mandatory to build a fully working product?",
                ans: "The focus is on building meaningful progress toward a working solution. While a complete product may not always be required, teams are expected to demonstrate logic, feasibility, and real world applicability.",
              },
              {
                que: "How will communication and updates be shared during the program?",
                ans: "Key updates, timelines, and instructions will be shared through the Udbhav platform and official communication channels. Participants are advised to regularly check for updates.",
              },
              {
                que: "Can ideas be implemented after the program ends?",
                ans: "Yes. Promising ideas may be considered for further development, pilot implementation, or scaling, depending on business relevance and feasibility.",
              },
              {
                que: "Will participation in Udbhav impact my regular work responsibilities?",
                ans: "Udbhav runs alongside regular roles. While managers are encouraged to support participation, teams are expected to balance responsibilities and plan their time effectively.",
              },
              {
                que: "How does the Alumni Program fit into the Udbhav journey?",
                ans: "Participants whose ideas progress through Udbhav become part of the Alumni Program, enabling continued learning, collaboration, mentorship opportunities, and long term engagement beyond a single edition.",
              },
            ].map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.que}
                answer={faq.ans}
                index={index}
              />
            ))}
          </div>

          {/* <div className="mt-12 text-center" data-aos="fade-up" data-aos-delay="200">
      <p className="text-gray-300 mb-4 text-lg">Still have questions?</p>
  <button
      onClick={() => navigate("/help")}
      className="inline-block bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
    >
      Contact Us
    </button>
    </div> */}
        </div>
      </section>
    </div>
  );
};

export default Home;
